/**
 * RadioPlayerContext
 * Manages the currently playing radio station across the app
 */

import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { getBackendUrl } from '../utils/api';

const RadioPlayerContext = createContext(null);

export const useRadioPlayer = () => {
  const context = useContext(RadioPlayerContext);
  if (!context) {
    throw new Error('useRadioPlayer must be used within RadioPlayerProvider');
  }
  return context;
};

export const RadioPlayerProvider = ({ children }) => {
  const [currentStation, setCurrentStation] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.7);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [nowPlaying, setNowPlaying] = useState(null); // Current song metadata
  const audioRef = useRef(null);
  const metadataIntervalRef = useRef(null);

  // Fetch LIVE now-playing metadata directly from the station's stream
  const fetchNowPlaying = useCallback(async (station) => {
    if (!station) return;
    
    // PRIMARY: Fetch LIVE ICY metadata directly from the stream (real-time, no cache)
    if (station.stream_url) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000);
        
        const response = await fetch(
          `${getBackendUrl()}/api/radio/metadata?stream_url=${encodeURIComponent(station.stream_url)}`,
          { signal: controller.signal }
        );
        clearTimeout(timeoutId);
        
        if (response.ok) {
          const data = await response.json();
          if (data.source !== 'no-icy-support' && (data.artist || data.title)) {
            setNowPlaying(prev => {
              const newArtist = data.artist || '';
              const newTitle = data.title || '';
              if (prev?.artist === newArtist && prev?.title === newTitle) return prev;
              return { artist: newArtist, title: newTitle, full_title: data.fullText };
            });
            return;
          }
        }
      } catch (err) {
        // Timeout or network error — fall through to cached fallback
      }
    }
    
    // FALLBACK: Use cached realtime data (updates every ~3 min)
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const response = await fetch(`${getBackendUrl()}/api/stream-monitor/realtime`, {
        headers: { 'Authorization': `Bearer ${token}` },
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        const stationData = data.stations?.find(s => s.station_id === station._id);
        if (stationData?.now_playing) {
          setNowPlaying(prev => {
            const newArtist = stationData.now_playing.artist || '';
            const newTitle = stationData.now_playing.title || '';
            if (prev?.artist === newArtist && prev?.title === newTitle) return prev;
            return {
              artist: newArtist,
              title: newTitle,
              full_title: stationData.now_playing.full_title,
              listeners: stationData.now_playing.listeners,
            };
          });
        }
      }
    } catch (err) {
      // Both sources failed — keep showing last known metadata
    }
  }, []);

  // Start/stop metadata polling when station changes
  useEffect(() => {
    if (currentStation && isPlaying) {
      // Immediate first fetch (no delay)
      fetchNowPlaying(currentStation);
      
      // Poll every 10 seconds for responsive updates
      metadataIntervalRef.current = setInterval(() => {
        fetchNowPlaying(currentStation);
      }, 10000);
      
      return () => {
        if (metadataIntervalRef.current) {
          clearInterval(metadataIntervalRef.current);
          metadataIntervalRef.current = null;
        }
      };
    }
    
    return () => {
      if (metadataIntervalRef.current) {
        clearInterval(metadataIntervalRef.current);
        metadataIntervalRef.current = null;
      }
    };
  }, [currentStation, isPlaying, fetchNowPlaying]);

  // Initialize audio element
  useEffect(() => {
    audioRef.current = new Audio();
    audioRef.current.volume = volume;
    
    // Event listeners
    const audio = audioRef.current;
    
    const handleCanPlay = () => {
      setIsLoading(false);
      setError(null);
    };
    
    const handleError = (e) => {
      setIsLoading(false);
      setError('Δεν ήταν δυνατή η αναπαραγωγή του stream');
      setIsPlaying(false);
      console.error('Audio error:', e);
    };
    
    const handleWaiting = () => {
      setIsLoading(true);
    };
    
    const handlePlaying = () => {
      setIsLoading(false);
      setIsPlaying(true);
    };
    
    const handleEnded = () => {
      setIsPlaying(false);
    };
    
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('error', handleError);
    audio.addEventListener('waiting', handleWaiting);
    audio.addEventListener('playing', handlePlaying);
    audio.addEventListener('ended', handleEnded);
    
    return () => {
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('waiting', handleWaiting);
      audio.removeEventListener('playing', handlePlaying);
      audio.removeEventListener('ended', handleEnded);
      audio.pause();
      audio.src = '';
    };
  }, []);

  // Update volume when changed
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  const playStation = useCallback((station) => {
    if (!station?.stream_url) {
      setError('Δεν υπάρχει διαθέσιμο stream URL');
      return;
    }
    
    const audio = audioRef.current;
    
    // If same station, just toggle play/pause
    if (currentStation?._id === station._id && audio.src) {
      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
      } else {
        audio.play().catch(e => {
          setError('Σφάλμα αναπαραγωγής');
          console.error('Play error:', e);
        });
      }
      return;
    }
    
    // New station
    setCurrentStation(station);
    setNowPlaying(null); // Reset now playing
    setIsLoading(true);
    setError(null);
    
    // Stop current playback
    audio.pause();
    
    // Determine stream URL
    // Always use the ICY-aware proxy for:
    //   1. HTTP streams on HTTPS pages (mixed content blocking)
    //   2. Known ICY/SHOUTcast servers that embed metadata in the stream
    //      (e.g. OnWeb s3.onweb.gr — metadata bytes corrupt HTML5 audio)
    let streamUrl = station.stream_url;
    const isHttpsPage = window.location.protocol === 'https:';
    const isHttpStream = streamUrl?.startsWith('http://');
    // Hosts that serve Icecast/SHOUTcast streams with ICY metadata bytes
    // embedded in the audio. The browser's HTML5 audio cannot strip
    // those bytes, so playback is either silent or breaks the codec.
    // We route them through the ICY-aware proxy on the backend.
    const isIcyServer = streamUrl?.includes('onweb.gr') ||
                        streamUrl?.includes('live24.gr') ||
                        streamUrl?.includes('streamupsolutions.com') ||
                        streamUrl?.includes('dedicateware.com') ||
                        streamUrl?.includes('streamtheworld.com') ||
                        streamUrl?.includes('shoutcaststreaming.com') ||
                        streamUrl?.includes(':8000') ||
                        streamUrl?.includes(':8888') ||
                        streamUrl?.includes(':9000') ||
                        streamUrl?.includes('shoutcast') ||
                        streamUrl?.includes('icecast') ||
                        streamUrl?.endsWith('/;');   // SHOUTcast legacy URL pattern
    
    if (station._id && (isIcyServer || (isHttpsPage && isHttpStream))) {
      // Use ICY-aware proxy: strips metadata blocks, solves mixed content
      streamUrl = `${getBackendUrl()}/api/stream-monitor/stream-proxy/${station._id}`;
      console.log('Using ICY-aware stream proxy:', isIcyServer ? 'ICY server' : 'HTTP on HTTPS');
    }
    
    // Set new source and play
    audio.src = streamUrl;
    audio.load();
    audio.play().catch(e => {
      setIsLoading(false);
      setError('Δεν ήταν δυνατή η αναπαραγωγή');
      console.error('Play error:', e);
    });
  }, [currentStation, isPlaying]);

  const stopPlayback = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    setIsPlaying(false);
    setCurrentStation(null);
    setNowPlaying(null);
    setError(null);
  }, []);

  const togglePlayPause = useCallback(() => {
    if (!audioRef.current || !currentStation) return;
    
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().catch(e => {
        setError('Σφάλμα αναπαραγωγής');
        console.error('Play error:', e);
      });
    }
  }, [isPlaying, currentStation]);

  const changeVolume = useCallback((newVolume) => {
    setVolume(Math.max(0, Math.min(1, newVolume)));
  }, []);

  const value = {
    currentStation,
    isPlaying,
    isLoading,
    error,
    volume,
    nowPlaying,
    playStation,
    stopPlayback,
    togglePlayPause,
    changeVolume,
  };

  return (
    <RadioPlayerContext.Provider value={value}>
      {children}
    </RadioPlayerContext.Provider>
  );
};

export default RadioPlayerContext;
